import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { Upload as UploadIcon } from "@mui/icons-material";
import Toolbar from "../base/Toolbar";
import DataTable from "../base/DataTable";
import axios from "axios"; // Импорт axios для выполнения HTTP-запросов
import api from "../../api/api";
import staffActions from "./StaffActions";
import ColumnsModal from "../base/ColumnsModal"; //моадльное окно колонок
import FiltersModal from "../base/FiltersModal";
import filtersConfig from "../config/filtersConfig"; // Импорт конфигурации фильтров
import { useErrorHandler } from "../../../hooks/useErrorHandler";

const StaffPage = () => {
  const [staff, setStaff] = useState([]);
  const [columns, setColumns] = useState([]); // Конфигурация колонок
  const [viewMode, setViewMode] = useState("list");
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false); // Состояние для модального окна
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false); // Состояние для открытия модал
  const [selectedItems, setSelectedItems] = useState([]); // Состояние выбранных элементов
  // Убираем businessTypeKey - больше не нужен
  // const [businessTypeKey, setBusinessTypeKey] = useState(null);
  const [expandedStaffId, setExpandedStaffId] = useState(null); // ID выбранного сотрудника
  const navigate = useNavigate(); // Инициализация navigate
  const { handleError, showSuccess } = useErrorHandler();
  const tableKey = "staff"; // Указываем ключ таблицы для текущей страницы
  const filtersConfigForStaff = filtersConfig[tableKey]; // Базовая конфигурация фильтров для staff
  const [filters, setFilters] = useState([]); // Состояние для хранения полученных фильтров

  // Загрузка сотрудников
  const fetchStaff = async () => {
    try {
      const response = await api.get("/staff/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      // Добавляем full_name к каждому сотруднику
      const staffWithFullName = response.data.map(staff => {
        // Создаем full_name из фамилии, имени и отчества
        const nameParts = [];
        if (staff.last_name) nameParts.push(staff.last_name);
        if (staff.first_name) nameParts.push(staff.first_name);
        if (staff.middle_name) nameParts.push(staff.middle_name);
        
        return {
          ...staff,
          full_name: nameParts.length > 0 ? nameParts.join(' ') : 'Не указано',
          rental_point_name: staff.rental_point?.name || 'Не назначен'
        };
      });
      
      setStaff(staffWithFullName);
    } catch (error) {
      console.error("Ошибка загрузки сотрудников:", error);
      handleError(error, "Не удалось загрузить список сотрудников.");
    }
  };

  // Загрузка конфигурации колонок
  const fetchColumns = async () => {
    try {
      const response = await api.get("/table-columns/", {
        params: { table_key: "staff" },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Преобразуем конфигурацию без фильтрации по видимости
      const transformedColumns = response.data.map((col) => ({
        id: col.id, // ID для работы с колонками
        table_key: col.table_key, // Ключ таблицы
        column_name: col.column_name, // Название колонки
        field: col.column_name,
        label: translateLabelToRussian(col.column_name), // Заменяем label
        sortable: col.sortable,
        is_visible: col.is_visible, // Добавляем поле видимости для фильтрации в DataTable
      }));

      setColumns(transformedColumns); // Передаём весь конфиг
    } catch (error) {
      console.error("Ошибка загрузки конфигурации колонок:", error);
      handleError(error, "Не удалось загрузить конфигурацию таблицы.");
    }
  };

  // Функция для перевода на русский язык
  const translateLabelToRussian = (columnName) => {
    const translations = {
      id: "Идентификатор",
      staff_number: "Номер сотрудника",
      email: "Электронная почта",
      hashed_password: "Хэшированный пароль",
      first_name: "Имя",
      last_name: "Фамилия",
      middle_name: "Отчество",
      full_name: "ФИО",
      phone: "Телефон",
      date_of_birth: "Дата рождения",
      address: "Адрес",
      image_url: "Ссылка на изображение",
      role: "Роль",
      passport_series: "Серия паспорта",
      passport_number: "Номер паспорта",
      passport_issue_date: "Дата выдачи паспорта",
      passport_issued_by: "Кем выдан паспорт",
      passport_division_code: "Код подразделения",
      company_id: "ID компании",
      created_at: "Дата создания",
      rental_point_name: "Пункт проката",
    };
    return translations[columnName] || columnName;
  };

  // Удаление сотрудника
  const handleDelete = async (id) => {
    try {
      await api.delete(`/staff/remove/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      showSuccess("Сотрудник успешно удален!");
      fetchStaff(); // Обновляем список после удаления
    } catch (error) {
      console.error("Ошибка удаления сотрудника:", error);
      handleError(error, "Не удалось удалить сотрудника. Возможно, он связан с другими данными.");
    }
  };

  // Удаление выбранных элементов
  const handleDeleteSelected = async () => {
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (let id of selectedItems) {
        try {
          await api.delete(`/staff/remove/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          successCount++;
        } catch (err) {
          console.error(`Ошибка удаления сотрудника ${id}:`, err);
          errorCount++;
        }
      }
      
      setSelectedItems([]); // Очищаем выбранные элементы
      fetchStaff(); // Обновляем список после удаления
      
      if (successCount > 0 && errorCount === 0) {
        showSuccess(`Успешно удалено сотрудников: ${successCount}`);
      } else if (successCount > 0 && errorCount > 0) {
        handleError(new Error(`Удалено: ${successCount}, ошибок: ${errorCount}`), 
          `Удалено: ${successCount} сотрудников, не удалось удалить: ${errorCount}`);
      } else {
        handleError(new Error("Не удалось удалить выбранных сотрудников"), 
          "Не удалось удалить выбранных сотрудников. Возможно, они связаны с другими данными.");
      }
    } catch (error) {
      console.error("Ошибка удаления выбранных сотрудников:", error);
      handleError(error, "Произошла ошибка при удалении выбранных сотрудников.");
    }
  };

  // Открыть сотрудника
  const toggleExpandStaff = (id) => {
    setExpandedStaffId((prevId) => (prevId === id ? null : id)); // Если ID уже выбран, свернуть; иначе раскрыть
  };
  // Загрузка бизнес-ключа - больше не нужна
  /*
  const fetchBusinessTypeKey = async () => {
    try {
      const profileResponse = await api.get("/settings/settings", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (profileResponse.data && profileResponse.data.business_type_key) {
        const { business_type_key, navbar_color, sidebar_color } =
          profileResponse.data;
        setBusinessTypeKey(business_type_key);
      } else {
        console.error("Данные не в ожидаемом формате");
      }
    } catch (error) {
      console.error("Ошибка загрузки ключа бизнеса:", error);
    }
  };
  */

  // выбираем какие колонки отображаются а какие нет.
  const handleColumnsChange = async (updatedConfig) => {
    try {
      console.log(
        "Отправляемые данные на сервер (без фильтрации):",
        updatedConfig
      );

      // Преобразуем колонки в формат для bulk update
      const updates = updatedConfig.map((col) => ({
        id: col.id,
        is_visible: col.is_visible,
      }));

      // Отправляем данные на сервер как есть
      await api.put("/table-columns/bulk-update-visibility", updates, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Обновляем локальное состояние
      setColumns(updatedConfig);
      showSuccess("Настройки колонок успешно сохранены!");
    } catch (error) {
      console.error("Ошибка сохранения конфигурации колонок:", error);
      handleError(error, "Не удалось сохранить настройки колонок.");
    }
  };

  // Проверка и создание фильтров при загрузке страницы
  const initializeFilters = async () => {
    console.log("🔧 Используем статичные фильтры для staff");

    // Статичные фильтры для staff
    const staticFilters = [
      {
        filter_name: "position",
        filter_type: "select",
        options: [
          { value: "manager", label: "Менеджер" },
          { value: "admin", label: "Администратор" },
          { value: "operator", label: "Оператор" },
        ],
        is_visible: true,
        table_key: "staff",
      },
      {
        filter_name: "status",
        filter_type: "select",
        options: [
          { value: "active", label: "Активный" },
          { value: "inactive", label: "Неактивный" },
        ],
        is_visible: true,
        table_key: "staff",
      },
    ];

    setFilters(staticFilters);
    console.log("✅ Статичные фильтры установлены");
  };

  // Обработка импорта сотрудников
  const handleImport = async () => {
    if (!importFile) {
      showSuccess("Выберите файл для импорта", "warning");
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await api.post("/staff/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      showSuccess(
        `Импортировано: ${response.data.imported}, Пропущено: ${response.data.skipped}`,
        "success"
      );
      
      setImportDialog(false);
      setImportFile(null);
      fetchStaff(); // Обновляем список
    } catch (error) {
      handleError(error, "Ошибка импорта сотрудников");
    } finally {
      setImporting(false);
    }
  };

  // Скачивание шаблона для импорта
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/staff/import/template", {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_staff.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess("Шаблон загружен", "success");
    } catch (error) {
      handleError(error, "Ошибка загрузки шаблона");
    }
  };

  useEffect(() => {
    // fetchBusinessTypeKey(); // Убираем - больше не нужно
    fetchStaff(); // Загружаем сотрудников
    fetchColumns(); // Загружаем колонки
    initializeFilters();
  }, []); // Пустой массив, чтобы вызвать только один раз при монтировании компонента

  return (
    <>
      {" "}
      <Toolbar
        onOpenColumnsModal={() => setIsColumnsModalOpen(true)} // Функция открытия модального окна
        onOpenFiltersModal={() => setIsFiltersModalOpen(true)}
        currentPage="staff"
        // businessTypeKey={businessTypeKey} // Убираем - больше не нужно
        onAddClick={() => navigate("/staff/edit/new")}
        onViewChange={(view) => setViewMode(view)} // Меняем viewMode
        onRefresh={fetchStaff} // Обновление данных
        onExport={() => console.log("Экспорт данных")} // Экспорт данных
        totalRecords={staff.length} // Количество записей
        onDeleteSelected={handleDeleteSelected} // Передаем функцию удаления выбранных
        selectedItems={selectedItems} // Передаем выбранные элементы
        customButtons={[
          {
            label: "Импорт",
            icon: <UploadIcon />,
            onClick: () => setImportDialog(true),
          },
        ]}
      />
      <DataTable
        selectedItems={selectedItems}
        data={staff} // Данные сотрудников
        config={columns.filter((col) => col.is_visible)} // Фильтрация видимых колонок
        viewMode={viewMode} // Текущий режим отображения
        onDelete={handleDelete} // Функция удаления
        onToolbarData={({ selectedItems }) => setSelectedItems(selectedItems)} // Обновляем выбранные элементы
        actions={staffActions} // Передаём массив действий
      />
      <ColumnsModal
        tableKey={tableKey}
        onReload={fetchColumns}
        open={isColumnsModalOpen}
        onClose={() => setIsColumnsModalOpen(false)}
        config={columns} // Передача всех колонок
        onColumnsChange={handleColumnsChange} // Сохраняем сразу в базу данных
      />
      <FiltersModal
        open={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        filters={filters} // Передача всех колонок
        tableKey={tableKey}
        //   onFiltersChange={handleFiltersChange} // Сохраняем сразу в базу данных
      />

      {/* Диалог импорта */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Импорт сотрудников из файла</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleDownloadTemplate}
              sx={{ mb: 2 }}
            >
              Скачать шаблон Excel
            </Button>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Заполните шаблон и загрузите его для импорта сотрудников.
          </Typography>
          <Typography variant="caption" component="div" sx={{ mb: 2, color: 'text.secondary' }}>
            <strong>Два способа заполнения имени:</strong><br/>
            1. Заполните колонку "ФИО" целиком (Иванов Иван Иванович)<br/>
            2. Или заполните отдельно "Фамилия", "Имя", "Отчество"<br/>
            <br/>
            <strong>Обязательные поля:</strong> ФИО (любой из способов), Email, Логин<br/>
            <strong>Дополнительные:</strong> Телефон, Адрес, Должность, Пункт проката, Паспортные данные
          </Typography>
          
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
          >
            Выбрать файл
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setImportFile(e.target.files[0])}
            />
          </Button>
          
          {importFile && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Выбран файл: {importFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)} disabled={importing}>
            Отмена
          </Button>
          <Button 
            onClick={handleImport} 
            variant="contained" 
            disabled={!importFile || importing}
          >
            {importing ? "Импорт..." : "Импортировать"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StaffPage;
